import { useState, useMemo } from "react";
import { PageHero, SectionHeading, Eyebrow } from "@/components/site/Sections";
import { CtaButton } from "@/components/site/CtaButton";
import { Reveal } from "@/components/site/Reveal";
import { FINANCING_PRODUCTS, FINANCING_STEPS, FINANCING_DISCLAIMER, IMAGES } from "@/data/content";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, Info } from "lucide-react";

const SHOW_CALCULATOR = false;

const PRODUCTS = [
  { key: "dscr", label: "DSCR (30-yr amortized)", rate: 6.75, ltv: 0.8, amortize: true },
  { key: "rental", label: "Rental (30-yr amortized)", rate: 6.5, ltv: 0.75, amortize: true },
  { key: "hard-money", label: "Hard Money (interest-only)", rate: 11.0, ltv: 0.85, amortize: false },
  { key: "fix-and-flip", label: "Fix & Flip (interest-only)", rate: 9.25, ltv: 0.9, amortize: false },
  { key: "bridge", label: "Bridge (interest-only)", rate: 8.5, ltv: 0.75, amortize: false },
];

const fmt = (n) =>
  isFinite(n) ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "—";

export default function FinancingHub() {
  const [purchase, setPurchase] = useState(150000);
  const [rehab, setRehab] = useState(30000);
  const [rent, setRent] = useState(1600);
  const [productKey, setProductKey] = useState("dscr");

  const result = useMemo(() => {
    const p = PRODUCTS.find((x) => x.key === productKey);
    const basis = Number(purchase) + Number(rehab);
    const maxLoan = Number(purchase) * p.ltv;
    const monthlyRate = p.rate / 100 / 12;
    let payment;
    if (p.amortize) {
      const n = 360;
      payment = (maxLoan * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -n));
    } else {
      payment = maxLoan * monthlyRate;
    }
    const dscr = payment > 0 ? Number(rent) / payment : 0;
    const fees = basis * 0.03;
    const cashToClose = Math.max(0, basis - maxLoan) + fees;
    return { maxLoan, payment, dscr, cashToClose, rate: p.rate };
  }, [purchase, rehab, rent, productKey]);

  return (
    <div>
      <PageHero
        eyebrow="Vertexhub Investor Capital Hub"
        title="Need Funding for Your Next Property?"
        subtitle="Explore investor-focused financing options for rentals, auction acquisitions, rehabs and portfolio growth. Vertexhub helps coordinate financing — we are not a bank or direct lender."
        image={IMAGES.facade}
      >
        <CtaButton to="/request-financing" size="lg">Request Financing Support</CtaButton>
      </PageHero>

      {/* Products */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeading eyebrow="Loan Programs" title="See Available Loan Programs" />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(FINANCING_PRODUCTS).map(([slug, f], i) => (
              <Reveal key={slug} delay={i * 60}>
                <Link to={`/financing/${slug}`} className="group flex h-full flex-col rounded-xl border border-slate-200 bg-white p-7 transition-all hover:border-gold/40 hover:bg-white">
                  <div className="flex items-center justify-between">
                    <Eyebrow>{f.rate}</Eyebrow>
                    <ArrowRight className="h-4 w-4 text-slate-400 transition-all group-hover:translate-x-1 group-hover:text-gold" />
                  </div>
                  <h3 className="mt-3 text-lg font-semibold text-[#0B132B]">{f.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500">{f.intro}</p>
                  <p className="mt-4 font-mono-x text-xs text-gold/80">{f.ltv}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {SHOW_CALCULATOR && (
      <section className="border-y border-slate-200 bg-[#F5F3EC] py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <SectionHeading center eyebrow="Interactive Estimator" title="DSCR & Hard Money Loan Calculator" subtitle="Estimates only — actual terms are set by the lender and program." />
          <div data-testid="financing-loan-calculator-container" className="mt-12 grid gap-8 rounded-2xl border border-slate-200 bg-[#FAF8F1] p-8 lg:grid-cols-2 lg:p-10">
            <div className="space-y-6">
              <NumberField label="Purchase Price" value={purchase} onChange={setPurchase} testid="calc-purchase" />
              <NumberField label="Estimated Rehab Cost" value={rehab} onChange={setRehab} testid="calc-rehab" />
              <NumberField label="Expected Monthly Rent" value={rent} onChange={setRent} testid="calc-rent" />
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-600">Loan Product</label>
                <select
                  data-testid="calc-product"
                  value={productKey}
                  onChange={(e) => setProductKey(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-[#0B132B] focus:border-gold focus:outline-none"
                >
                  {PRODUCTS.map((p) => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col justify-center gap-4 rounded-xl border border-gold/25 bg-white p-8">
              <div className="flex items-center gap-2 text-gold">
                <Calculator className="h-5 w-5" />
                <span className="font-mono-x text-xs uppercase tracking-widest">Estimated Output</span>
              </div>
              <Output label="Estimated Monthly Payment" value={fmt(result.payment)} />
              <Output label="Max Loan Amount" value={fmt(result.maxLoan)} />
              <Output label="DSCR Ratio" value={result.dscr.toFixed(2)} highlight={result.dscr >= 1.2} />
              <Output label="Estimated Cash-to-Close" value={fmt(result.cashToClose)} />
              <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                Based on a {result.rate}% sample rate. DSCR ≥ 1.20 is often viewed favorably; requirements vary by lender.
              </p>
              <CtaButton to="/request-financing" data-testid="financing-calc-submit-btn" className="mt-2">
                Review My Deal
              </CtaButton>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Process */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeading center eyebrow="The Financing Process" title="Five Simple Steps" />
          <div className="mt-12 grid gap-4 md:grid-cols-5">
            {FINANCING_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 70}>
                <div className="h-full rounded-xl border border-slate-200 bg-white p-6">
                  <div className="font-display text-3xl font-bold text-gold/70">{s.n}</div>
                  <h4 className="mt-3 text-sm font-semibold text-[#0B132B]">{s.title}</h4>
                  <p className="mt-2 text-xs leading-relaxed text-slate-500">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Auction financing */}
      <section className="border-y border-slate-200 bg-[#F5F3EC] py-16">
        <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
          <Eyebrow>Financing an Auction Purchase?</Eyebrow>
          <h3 className="mt-3 font-display text-2xl font-bold text-[#0B132B] sm:text-3xl">Start Before Auction Day.</h3>
          <p className="mx-auto mt-4 max-w-2xl text-sm text-slate-500">
            Auction properties can have strict deposit and payment deadlines. Don't wait until after winning
            the auction to explore financing — organize your transaction information and financing resources first.
          </p>
          <div className="mt-6">
            <CtaButton to="/request-financing">Review My Auction Financing Options</CtaButton>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-[#FAF8F1] p-5">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
            <p className="text-xs leading-relaxed text-slate-500">{FINANCING_DISCLAIMER}</p>
          </div>
        </div>
      </section>
    </div>
  );
}

const NumberField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="mb-2 block text-sm font-medium text-slate-600">{label}</label>
    <div className="flex items-center rounded-lg border border-slate-300 bg-white focus-within:border-gold">
      <span className="px-3 text-slate-500">$</span>
      <input
        data-testid={testid}
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        className="w-full bg-transparent py-3 pr-4 text-sm text-[#0B132B] focus:outline-none"
      />
    </div>
  </div>
);

const Output = ({ label, value, highlight }) => (
  <div className="flex items-center justify-between border-b border-slate-200 pb-2">
    <span className="text-sm text-slate-500">{label}</span>
    <span className={`font-mono-x text-lg font-semibold ${highlight ? "text-green-400" : "text-[#0B132B]"}`}>{value}</span>
  </div>
);
